import React, { useCallback, useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import api from "api";
import Admins from "./Admins/admins";
import Dashboard from "./Dashboard/dashboard";
import Users from "./Users/users";
import {
  getCurrentAdminEmail,
  getCurrentAdminId,
  endpointMap,
  normalizeCount,
  normalizeList,
  normalizeStats,
  normalizeDashboard,
} from "./adminUtils";
import AdminLayout from "./components/AdminLayout";
import "./adminPanel.css";

const PAGE_SIZE = 10;

const AdminPanel = () => {
  const location = useLocation();
  const currentAdminEmail = getCurrentAdminEmail();
  const currentAdminId = getCurrentAdminId();

  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAdmins: 0,
    blockedUsers: 0,
  });
  const [dashboard, setDashboard] = useState({ summary: {}, charts: {}, filter: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userPage, setUserPage] = useState(1);
  const [adminPage, setAdminPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [adminTotal, setAdminTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [adminSearch, setAdminSearch] = useState("");

  const fetchAdminData = useCallback(async (uPage = 1, aPage = 1, dateParams = {}, uSearch = "", aSearch = "") => {
    setLoading(true);
    setError("");

    try {
      const [usersResponse, adminsResponse, dashboardResponse] = await Promise.allSettled([
        api.get(endpointMap.users.list, { params: { page: uPage, limit: PAGE_SIZE, search: uSearch || undefined } }),
        api.get(endpointMap.admins.list, { params: { page: aPage, limit: PAGE_SIZE, search: aSearch || undefined } }),
        api.get(endpointMap.dashboard, { params: dateParams }),
      ]);

      const nextUsers =
        usersResponse.status === "fulfilled" ? normalizeList(usersResponse.value, "users") : [];
      const nextAdmins =
        adminsResponse.status === "fulfilled"
          ? normalizeList(adminsResponse.value, "admins")
          : [];

      setUsers(nextUsers);
      setAdmins(nextAdmins);

      if (usersResponse.status === "fulfilled") {
        const total = normalizeCount(usersResponse.value);
        if (total !== null) setUserTotal(total);
      }
      if (adminsResponse.status === "fulfilled") {
        const total = normalizeCount(adminsResponse.value);
        if (total !== null) setAdminTotal(total);
      }

      setStats(
        dashboardResponse.status === "fulfilled"
          ? normalizeStats(dashboardResponse.value, nextUsers, nextAdmins)
          : normalizeStats(null, nextUsers, nextAdmins)
      );

      if (dashboardResponse.status === "fulfilled") {
        setDashboard(normalizeDashboard(dashboardResponse.value));
      }

      if (usersResponse.status === "rejected" || adminsResponse.status === "rejected") {
        setError("Some admin records could not be loaded.");
      }
    } catch {
      setError("Admin data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminData(1, 1);
  }, [fetchAdminData]);

  const loadUsers = useCallback(async (newPage, search) => {
    setUserPage(newPage);
    setLoading(true);
    setError("");
    try {
      const res = await api.get(endpointMap.users.list, {
        params: { page: newPage, limit: PAGE_SIZE, search: search || undefined },
      });
      setUsers(normalizeList(res, "users"));
      const total = normalizeCount(res);
      if (total !== null) setUserTotal(total);
    } catch {
      setError("Failed to load page.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmins = useCallback(async (newPage, search) => {
    setAdminPage(newPage);
    setLoading(true);
    setError("");
    try {
      const res = await api.get(endpointMap.admins.list, {
        params: { page: newPage, limit: PAGE_SIZE, search: search || undefined },
      });
      setAdmins(normalizeList(res, "admins"));
      const total = normalizeCount(res);
      if (total !== null) setAdminTotal(total);
    } catch {
      setError("Failed to load page.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUserPageChange = (newPage) => loadUsers(newPage, userSearch);
  const handleAdminPageChange = (newPage) => loadAdmins(newPage, adminSearch);

  const handleUserSearch = (search) => {
    setUserSearch(search);
    loadUsers(1, search);
  };

  const handleAdminSearch = (search) => {
    setAdminSearch(search);
    loadAdmins(1, search);
  };

  const runMutation = async (request, successMessage) => {
    setError("");
    try {
      await request();
      toast.success(successMessage, {
        position: "top-center",
        containerId: "main",
        theme: "light",
      });
      await fetchAdminData(userPage, adminPage, {}, userSearch, adminSearch);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.response?.data?.error || "Request failed.";
      setError(message);
      toast.error(message, {
        position: "top-center",
        containerId: "main",
        theme: "light",
      });
      throw err;
    }
  };

  const createRecord = (type, payload) =>
    runMutation(
      () => api.post(endpointMap[type].create, payload),
      "Record created successfully."
    );

  const updateRecord = (type, id, payload) =>
    runMutation(
      () => api.put(`${endpointMap[type].update}/${id}`, payload),
      "Record updated successfully."
    );

  const deleteRecord = (type, id) =>
    runMutation(
      () => api.delete(`${endpointMap[type].remove}/${id}`),
      "Record deleted successfully."
    );

  // Pause / resume a member. A paused member is hidden from the member list
  // and stops receiving new service requests.
  const setPausedRecord = (id, paused) =>
    runMutation(
      () =>
        api.put(
          `${endpointMap.users.pause}/${id}/${paused ? "pause" : "resume"}`
        ),
      paused ? "Account paused." : "Account resumed."
    );

  return (
    <AdminLayout>
      <Switch location={location}>
        <Route exact path="/admin">
          <Dashboard
            admins={admins}
            dashboard={dashboard}
            error={error}
            loading={loading}
            onRefresh={(dateParams) => fetchAdminData(userPage, adminPage, dateParams, userSearch, adminSearch)}
            stats={stats}
            users={users}
          />
        </Route>
        <Route exact path="/admin/users">
          <Users
            error={error}
            loading={loading}
            onCreate={(payload) => createRecord("users", payload)}
            onDelete={(id) => deleteRecord("users", id)}
            onPageChange={handleUserPageChange}
            onSetPaused={setPausedRecord}
            onRefresh={() => fetchAdminData(userPage, adminPage, {}, userSearch, adminSearch)}
            onSearch={handleUserSearch}
            onUpdate={(id, payload) => updateRecord("users", id, payload)}
            page={userPage}
            pageSize={PAGE_SIZE}
            records={users}
            total={userTotal}
          />
        </Route>
        <Route exact path="/admin/admins">
          <Admins
            currentAdminEmail={currentAdminEmail}
            currentAdminId={currentAdminId}
            error={error}
            loading={loading}
            onCreate={(payload) => createRecord("admins", payload)}
            onDelete={(id) => deleteRecord("admins", id)}
            onPageChange={handleAdminPageChange}
            onRefresh={() => fetchAdminData(userPage, adminPage, {}, userSearch, adminSearch)}
            onSearch={handleAdminSearch}
            onUpdate={(id, payload) => updateRecord("admins", id, payload)}
            page={adminPage}
            pageSize={PAGE_SIZE}
            records={admins}
            total={adminTotal}
          />
        </Route>
        <Redirect to="/admin" />
      </Switch>
    </AdminLayout>
  );
};

export default AdminPanel;
